import { serverURL } from "@/utils/serverURL";
import type { SubstanceLensInfo } from "@/utils/types/substance";
import type { VizPayload } from "./workbench_api";
// DEMO (impl-demo-mock) — remove with services/demo.
import type { DemoClientHint } from "@/services/demo/hint";

// ── Wire types — mirror the server payloads in apps/nos/server/src/routes/
// me/flavor/formulas/. Keep these in lock-step when editing the server. ────

// "mol%" is set automatically by Auto-Formulate (nblock returns molar
// ratios). Conversion math against the other units isn't implemented yet —
// mol% rows round-trip verbatim and the FlaskRow cycle button is disabled
// for them so a stray click can't reinterpret the value as ppm/ppb.
export type AmountUnit = "ppm" | "ppb" | "ppt" | "mol%" | "mg" | "g" | "kg" | "μL" | "mL" | "L";

export type SubstanceKind = "compound" | "food";

export type ItemCategory = "FLAVOR" | "FIXED_INGREDIENT";

export type FlavorFormulaItemDto = {
  id: number;
  compoundId: string;          // "compound:<rowid>" | "food:<rowid>"
  category: ItemCategory;
  substanceKind: SubstanceKind;
  substanceId: number;
  substanceName: string | null;
  substanceCas: string | null;
  // NULL on FIXED_INGREDIENT rows whose concentration is still TBD; the
  // server's chk_flavor_amount_required guarantees FLAVOR rows carry both.
  amountValue: number | null;
  // Server emits as string | null (Drizzle's numeric/enum typing). The DB-level
  // amount_unit enum guarantees the value falls within AmountUnit at write
  // time, so this narrowing is safe. If a pre-migration row or data anomaly
  // ever surfaces a value outside the enum, downstream consumers (FlaskRow,
  // useAutosaveFormula) would see a string TS thinks is AmountUnit — fix at
  // the wire boundary if that ever becomes a real concern.
  amountUnit: AmountUnit | null;
  // Per-substance lens info from the server's substanceLens resolver. The
  // client uses these for lens grouping + family color + chem-tag in
  // FlaskPanel/FlaskRow.
  lens: SubstanceLensInfo;
};

export type FlavorFormulaDto = {
  id: number;
  flavorId: string;
  formulaNumber: number;
  name: string;
  prompt: string | null;
  parentFormulaId: number | null;
  sortOrder: number;
  currentVersionId: number | null;
  currentVersionNumber: number | null;
  createdAt: string;
  updatedAt: string;
  items: FlavorFormulaItemDto[];
};

export type ListFormulasResponse = {
  formulas: FlavorFormulaDto[];
  // DEMO (impl-demo-mock) — null for non-demo flavors. Remove with services/demo.
  demo: DemoClientHint | null;
};

export type CreateFormulaBody = {
  name?: string;
  prompt?: string | null;
  parentFormulaId?: number | null;
};

export type CreateFormulaResponse = {
  formulaId: number;
  formulaNumber: number;
  name: string;
  versionId: number;
  versionNumber: number;
};

export type RenameFormulaResponse = { formulaId: number; name: string };

export type ArchiveFormulaResponse = { formulaId: number; archived: true };

export type SaveVersionItem = {
  category: ItemCategory;
  substanceKind: SubstanceKind;
  substanceId: number;
  // FIXED_INGREDIENT items may pass null/null to record "required, amount TBD";
  // FLAVOR items must pass non-null on both. Server validates via Zod refine.
  amountValue: number | null;
  amountUnit: AmountUnit | null;
};

export type SaveVersionBody = {
  saveType: "autosave" | "manual" | "named";
  commitMessage?: string | null;
  items: SaveVersionItem[];
};

export type SaveVersionResponse = {
  formulaId: number;
  versionId: number;
  versionNumber: number;
  contentHash: string;
  deduped: boolean;
  // The server awaits computeViz inline and returns the resulting payload
  // here. The client pre-populates the React Query cache for ["viz",
  // versionId] with this so no follow-up /viz fetch is required after
  // autosave. null on dedup (no new version) or on a viz compute failure
  // (client falls back to GET /viz polling).
  viz: VizPayload | null;
};

// ── Errors ────────────────────────────────────────────────────────────────

export class FlavorFormulaApiError extends Error {
  status: number;
  endpoint: string;
  constructor(status: number, endpoint: string) {
    super(`${endpoint} failed: ${status}`);
    this.name = "FlavorFormulaApiError";
    this.status = status;
    this.endpoint = endpoint;
  }
}

// ── Functions ─────────────────────────────────────────────────────────────

export async function fetchFlavorFormulas(
  flavorId: string,
  signal?: AbortSignal,
): Promise<ListFormulasResponse> {
  const res = await fetch(serverURL`/me/flavor/${flavorId}/formulas`, {
    credentials: "include",
    signal,
  });
  if (!res.ok) {
    throw new FlavorFormulaApiError(res.status, `flavor/${flavorId}/formulas`);
  }
  return (await res.json()) as ListFormulasResponse;
}

export async function createFlavorFormula(
  flavorId: string,
  body: CreateFormulaBody,
): Promise<CreateFormulaResponse> {
  const res = await fetch(serverURL`/me/flavor/${flavorId}/formulas`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new FlavorFormulaApiError(res.status, `flavor/${flavorId}/formulas POST`);
  }
  return (await res.json()) as CreateFormulaResponse;
}

export async function renameFlavorFormula(
  flavorId: string,
  formulaId: number,
  name: string,
): Promise<RenameFormulaResponse> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/formulas/${formulaId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    },
  );
  if (!res.ok) {
    throw new FlavorFormulaApiError(res.status, `flavor/${flavorId}/formulas/${formulaId} PATCH`);
  }
  return (await res.json()) as RenameFormulaResponse;
}

export async function archiveFlavorFormula(
  flavorId: string,
  formulaId: number,
): Promise<ArchiveFormulaResponse> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/formulas/${formulaId}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) {
    throw new FlavorFormulaApiError(res.status, `flavor/${flavorId}/formulas/${formulaId} DELETE`);
  }
  return (await res.json()) as ArchiveFormulaResponse;
}

export async function saveFlavorFormulaVersion(
  flavorId: string,
  formulaId: number,
  body: SaveVersionBody,
  signal?: AbortSignal,
): Promise<SaveVersionResponse> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/formulas/${formulaId}/versions`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    },
  );
  if (!res.ok) {
    throw new FlavorFormulaApiError(
      res.status,
      `flavor/${flavorId}/formulas/${formulaId}/versions POST`,
    );
  }
  return (await res.json()) as SaveVersionResponse;
}
